import { useMemo } from 'react';
import { Alert, Drawer, Empty, Tag } from 'antd';
import { MonitorCheck } from 'lucide-react';
import type { RoleGroup } from './types';
import { getAccessibleScreens } from '../../../config/routeTitles';
import { splitPermissions } from '../../../config/permissionModules';

interface Props {
    role: RoleGroup | null;
    onClose: () => void;
}

/**
 * Các màn hình nhân viên thuộc nhóm quyền vào được — để admin kiểm tra nhanh
 * "nhóm này mở những gì" mà không phải đọc từng checkbox.
 */
export default function AccessScopeDrawer({ role, onClose }: Props) {
    const { sections, screenCount, staleCount } = useMemo(() => {
        const { valid, stale } = splitPermissions(role?.permissions ?? []);
        const secs = getAccessibleScreens(valid);
        const business = secs.filter((s) => s.group !== 'Cá nhân');
        return {
            sections: secs,
            screenCount: business.reduce((n, s) => n + s.modules.reduce((m, x) => m + x.screens.length, 0), 0),
            staleCount: stale.length,
        };
    }, [role]);

    return (
        <Drawer
            open={!!role}
            onClose={onClose}
            width={window.innerWidth < 768 ? '100%' : 520}
            title={
                <div>
                    <div>Phạm vi truy cập: {role?.name}</div>
                    <div className="text-xs font-normal text-gray-500 mt-0.5">
                        Vào được {screenCount} màn hình nghiệp vụ
                    </div>
                </div>
            }
        >
            <Alert
                type="info"
                showIcon
                className="mb-4"
                message="Danh sách màn hình nhân viên thuộc nhóm này vào được."
                description="Quyền thao tác trong màn (Thêm, Sửa, Xóa, Duyệt...) xem ở mục Sửa nhóm quyền."
            />

            {staleCount > 0 && (
                <Alert
                    type="warning"
                    showIcon
                    className="mb-4"
                    message={`Nhóm còn ${staleCount} quyền cũ không còn dùng — mở Sửa nhóm quyền và lưu để dọn.`}
                />
            )}

            {screenCount === 0 && (
                <Empty className="my-6" description="Nhóm này chưa mở màn hình nghiệp vụ nào" />
            )}

            <div className="flex flex-col gap-5">
                {sections.map((section) => (
                    <div key={section.group}>
                        <div className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-2">
                            {section.group}
                        </div>
                        <div className="flex flex-col gap-2">
                            {section.modules.map((m) => (
                                <div key={m.module} className="border rounded-md px-3 py-2">
                                    <div className="flex items-center justify-between mb-1.5">
                                        <span className="font-medium text-gray-800">{m.module}</span>
                                        <Tag>{m.screens.length} màn</Tag>
                                    </div>
                                    <div className="flex flex-wrap gap-x-4 gap-y-1">
                                        {m.screens.map((s) => (
                                            <span key={s} className="flex items-center gap-1.5 text-sm text-gray-600">
                                                <MonitorCheck className="w-3.5 h-3.5 text-[#3e91ab]" />
                                                {s}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                ))}
            </div>
        </Drawer>
    );
}
